import { createContext, type ReactNode, useContext, useLayoutEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router';
import type { RouteConfig } from 'react-router-config';
import { deriveRouteProps, type RouteProps } from '../routes/utils/deriveRouteProps';
import { selectHasRouteError, selectIsDiscord } from '../selectors/app';
import { useStores } from './StoresProvider';

export const RoutePropsContext = createContext<RouteProps | null>(null);

interface RoutePropsProviderProps {
  children: ReactNode;
  routes: RouteConfig[];
}

export function RoutePropsProvider({ children, routes }: RoutePropsProviderProps) {
  // Under `ReduxAsyncConnect` this is the location whose data has finished loading, not the live
  // history location, so the props switch over together with the page they describe.
  const location = useLocation<{ itemToken?: string } | undefined>();
  const isDiscord = useSelector(selectIsDiscord);
  const hasRouteError = useSelector(selectHasRouteError);
  const setRouteProps = useStores().appStore.setRouteProps();
  const { pathname, search, state } = location;
  const itemToken = state?.itemToken;

  // biome-ignore lint/correctness/useExhaustiveDependencies: keyed on the parts of `location` that feed the derivation, so its identity changing alone doesn't produce new props
  const routeProps = useMemo(
    () => deriveRouteProps({ routes, location, isDiscord, hasRouteError }),
    [routes, pathname, search, itemToken, isDiscord, hasRouteError],
  );

  // Mirrored for code outside React. A layout effect runs before any passive effect in the tree,
  // which is where those readers can first be triggered from.
  useLayoutEffect(() => {
    setRouteProps(routeProps);
  }, [routeProps, setRouteProps]);

  return <RoutePropsContext.Provider value={routeProps}>{children}</RoutePropsContext.Provider>;
}

export function useRouteProps() {
  const routeProps = useContext(RoutePropsContext);

  if (!routeProps) {
    throw new Error('useRouteProps must be used within a RoutePropsProvider');
  }

  return routeProps;
}
